import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export type RangePreset = '24h' | '7d' | '30d' | '90d' | 'custom'

interface RangeCtx {
  preset: RangePreset
  setPreset: (p: RangePreset) => void
  range: { from: number; to: number }
  customFrom: number
  customTo: number
  setCustom: (from: number, to: number) => void
}

const Ctx = createContext<RangeCtx | null>(null)

const PRESET_DAYS: Record<Exclude<RangePreset, 'custom'>, number> = {
  '24h': 1, '7d': 7, '30d': 30, '90d': 90,
}

const STORAGE_KEY = 'lf_admin_range'

interface Persisted { preset: RangePreset; customFrom?: number; customTo?: number }

function readPersisted(initial: RangePreset): Persisted {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { preset: initial }
    const parsed = JSON.parse(raw) as Persisted
    if (['24h', '7d', '30d', '90d', 'custom'].includes(parsed.preset)) return parsed
  } catch { /* ignore */ }
  return { preset: initial }
}

export function DateRangeProvider({ children, initial = '30d' }: { children: ReactNode; initial?: RangePreset }) {
  const persisted = useMemo(() => readPersisted(initial), [initial])
  const [preset, setPreset] = useState<RangePreset>(persisted.preset)
  const [customFrom, setCustomFrom] = useState<number>(
    persisted.customFrom ?? Date.now() - 7 * 86_400_000,
  )
  const [customTo, setCustomTo] = useState<number>(persisted.customTo ?? Date.now())

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ preset, customFrom, customTo }))
    } catch { /* ignore */ }
  }, [preset, customFrom, customTo])

  const range = useMemo(() => {
    if (preset === 'custom') return { from: customFrom, to: customTo }
    const to = Date.now()
    const from = to - PRESET_DAYS[preset] * 86_400_000
    return { from, to }
  }, [preset, customFrom, customTo])

  const setCustom = (from: number, to: number) => {
    setCustomFrom(from)
    setCustomTo(to)
    setPreset('custom')
  }

  return (
    <Ctx.Provider value={{ preset, setPreset, range, customFrom, customTo, setCustom }}>
      {children}
    </Ctx.Provider>
  )
}

export function useDateRangeCtx(): RangeCtx {
  const v = useContext(Ctx)
  if (!v) throw new Error('useDateRangeCtx must be inside DateRangeProvider')
  return v
}
